import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";

import {
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
  List,
  Box,
  Typography,
  Tooltip,
  Badge,
  Zoom,
} from "@material-ui/core";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";

// Icons
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import CodeRoundedIcon from "@material-ui/icons/CodeRounded";
import EventIcon from "@material-ui/icons/Event";
import LocalOfferIcon from "@material-ui/icons/LocalOffer";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import PeopleIcon from "@material-ui/icons/People";
import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import LocalAtmIcon from "@material-ui/icons/LocalAtm";
import TableChartIcon from "@material-ui/icons/TableChart";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import {
  AccountTree,
  AllInclusive,
  AttachFile,
  DeviceHubOutlined,
} from "@material-ui/icons";
import { ShapeLine } from "@mui/icons-material";

import { i18n } from "../translate/i18n";
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { Can } from "../components/Can";
import { SocketContext } from "../context/Socket/SocketContext";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import usePlans from "../hooks/usePlans";

// ============================================
// 🎨 ESTILOS MODERNOS - INSPIRADO INTERCOM/HUBSPOT
// ============================================
const useStyles = makeStyles((theme) => ({
  // Container
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: theme.spacing(1.5),
    paddingTop: 0,
    transition: "padding 0.3s ease",
  },
  containerCollapsedMain: {
    padding: theme.spacing(1),
    paddingTop: 0,
  },

  // Navigation Wrapper
  navWrapper: {
    flex: 1,
    overflowY: "auto",
    overflowX: "hidden",
    paddingRight: theme.spacing(0.5),
    "&::-webkit-scrollbar": {
      width: 4,
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: alpha(theme.palette.primary.main, 0.2),
      borderRadius: 2,
    },
  },

  // Seção Header
  sectionLabel: {
    padding: theme.spacing(2, 1.5, 0.75, 1.5),
    opacity: 0.5,
  },
  sectionText: {
    fontSize: "0.65rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
  },

  // Menu Item Base
  menuItem: {
    borderRadius: 12,
    marginBottom: 2,
    padding: theme.spacing(1.25, 1.5),
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
      "& $menuIcon": {
        transform: "scale(1.15)",
      },
      "& $menuText": {
        color: theme.palette.primary.main,
      },
    },
  },

  // Menu Item Ativo
  menuItemActive: {
    backgroundColor: alpha(theme.palette.primary.main, 0.12),
    "&::after": {
      content: '""',
      position: "absolute",
      right: 8,
      top: "50%",
      transform: "translateY(-50%)",
      width: 4,
      height: 20,
      borderRadius: 4,
      background: "linear-gradient(180deg, #6366F1 0%, #8B5CF6 100%)",
    },
    "& $menuIcon": {
      color: theme.palette.primary.main,
      background: alpha(theme.palette.primary.main, 0.15),
    },
    "& $menuText": {
      color: theme.palette.primary.main,
      fontWeight: 600,
    },
  },

  // Menu Icon
  menuIcon: {
    minWidth: "auto",
    marginRight: theme.spacing(1.5),
    width: 38,
    height: 38,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    backgroundColor: alpha(theme.palette.text.secondary, 0.06),
    color: theme.palette.text.secondary,
    transition: "all 0.2s ease",
    "& svg": {
      fontSize: 20,
    },
  },

  // Menu Text
  menuText: {
    "& .MuiTypography-root": {
      fontSize: "0.9rem",
      fontWeight: 500,
      color: theme.palette.text.primary,
      transition: "color 0.2s ease",
    },
  },

  // Collapsed Icon Only
  menuItemCollapsed: {
    padding: theme.spacing(1.25),
    justifyContent: "center",
    minHeight: 48,
    "& $menuIcon": {
      marginRight: 0,
    },
  },


  // Badge Styles
  badge: {
    "& .MuiBadge-badge": {
      fontSize: "0.6rem",
      height: 16,
      minWidth: 16,
      fontWeight: 700,
    },
  },
  warningBadge: {
    position: "absolute",
    top: 8,
    right: 8,
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#EF4444",
    animation: "$pulse 2s infinite",
  },
  dotBadge: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 8,
    height: 8,
    borderRadius: "50%",
    backgroundColor: "#22C55E",
  },

  // Expand Icon
  expandIcon: {
    color: theme.palette.text.secondary,
    fontSize: 18,
    marginLeft: "auto",
    transition: "transform 0.3s ease",
  },
  expandIconOpen: {
    transform: "rotate(180deg)",
  },

  // Submenu
  submenu: {
    marginLeft: theme.spacing(2.5),
    marginTop: theme.spacing(0.5),
    paddingLeft: theme.spacing(2),
    borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
  },
  submenuItem: {
    borderRadius: 10,
    padding: theme.spacing(1, 1.5),
    marginBottom: 2,
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.06),
    },
  },
  submenuIcon: {
    minWidth: 28,
    color: theme.palette.text.secondary,
    "& svg": {
      fontSize: 16,
    },
  },
  submenuText: {
    "& .MuiTypography-root": {
      fontSize: "0.8rem",
      fontWeight: 500,
      color: theme.palette.text.secondary,
    },
  },

  // Footer
  footer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    marginTop: "auto",
  },
  footerContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    borderRadius: 10,
    background: alpha(theme.palette.primary.main, 0.06),
  },
  footerDot: {
    width: 6,
    height: 6,
    borderRadius: "50%",
    backgroundColor: "#22C55E",
  },
  footerText: {
    fontSize: "0.7rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },

  // Animações
  "@keyframes pulse": {
    "0%": { transform: "scale(1)", opacity: 1 },
    "50%": { transform: "scale(1.2)", opacity: 0.7 },
    "100%": { transform: "scale(1)", opacity: 1 },
  },

  // Color variants for icons
  iconGreen: { color: "#22C55E", backgroundColor: alpha("#22C55E", 0.1) },
  iconBlue: { color: "#3B82F6", backgroundColor: alpha("#3B82F6", 0.1) },
  iconPurple: { color: "#8B5CF6", backgroundColor: alpha("#8B5CF6", 0.1) },
  iconOrange: { color: "#F59E0B", backgroundColor: alpha("#F59E0B", 0.1) },
  iconPink: { color: "#EC4899", backgroundColor: alpha("#EC4899", 0.1) },
  iconTeal: { color: "#14B8A6", backgroundColor: alpha("#14B8A6", 0.1) },
  iconRed: { color: "#EF4444", backgroundColor: alpha("#EF4444", 0.1) },
  iconCyan: { color: "#06B6D4", backgroundColor: alpha("#06B6D4", 0.1) },
  iconIndigo: { color: "#6366F1", backgroundColor: alpha("#6366F1", 0.1) },
  iconGray: { color: "#64748B", backgroundColor: alpha("#64748B", 0.1) },
  iconLime: { color: "#84CC16", backgroundColor: alpha("#84CC16", 0.1) },
}));

// ============================================
// COMPONENTE MENU ITEM
// ============================================
const MenuItem = ({ 
  to, 
  icon, 
  label, 
  collapsed, 
  iconClass, 
  badge,
  dotBadge,
  warningBadge,
  onClick,
}) => {
  const classes = useStyles();
  const location = useLocation();
  const isActive = to && (location.pathname === to || location.pathname.startsWith(to + "/"));

  const content = (
    <ListItem
      button
      component={to ? RouterLink : "div"}
      to={to}
      onClick={onClick}
      className={`${classes.menuItem} ${isActive ? classes.menuItemActive : ""} ${collapsed ? classes.menuItemCollapsed : ""}`}
    >
      <ListItemIcon className={`${classes.menuIcon} ${iconClass || ""}`}>
        {icon}
      </ListItemIcon>
      {!collapsed && (
        <ListItemText primary={label} className={classes.menuText} />
      )}
      {warningBadge && <Box className={classes.warningBadge} />}
      {dotBadge && <Box className={classes.dotBadge} />}
    </ListItem>
  );

  if (collapsed) {
    return (
      <Tooltip title={label} placement="right" arrow TransitionComponent={Zoom}>
        {content}
      </Tooltip>
    );
  }

  return content;
};

// ============================================
// REDUCER
// ============================================
const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];
    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }
    return [...state, ...newChats];
  }
  if (action.type === "CHANGE_CHAT") {
    return state.map((chat) => 
      chat.id === action.payload.chat.id ? action.payload.chat : chat
    );
  }
  if (action.type === "RESET") return [];
  return state;
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const MainListItems = ({ drawerClose, collapsed }) => {
  const classes = useStyles();
  const history = useHistory();
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user } = useContext(AuthContext);

  // States
  const [connectionWarning, setConnectionWarning] = useState(false);
  const [openCampaigns, setOpenCampaigns] = useState(false);
  const [openFlows, setOpenFlows] = useState(false);
  const [showCampaigns, setShowCampaigns] = useState(false);
  const [showKanban, setShowKanban] = useState(false);
  const [showOpenAi, setShowOpenAi] = useState(false);
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showSchedules, setShowSchedules] = useState(false);
  const [showInternalChat, setShowInternalChat] = useState(false);
  const [showExternalApi, setShowExternalApi] = useState(false);
  const [hasUnreadChats, setHasUnreadChats] = useState(false);
  const [chats, dispatch] = useReducer(reducer, []);

  const { getPlanCompany } = usePlans();
  const socketManager = useContext(SocketContext);

  // Effects
  useEffect(() => {
    async function fetchData() {
      try {
        const companyId = user.companyId;
        const planConfigs = await getPlanCompany(undefined, companyId);
        setShowCampaigns(planConfigs.plan.useCampaigns);
        setShowKanban(planConfigs.plan.useKanban);
        setShowOpenAi(planConfigs.plan.useOpenAi);
        setShowIntegrations(planConfigs.plan.useIntegrations);
        setShowSchedules(planConfigs.plan.useSchedules);
        setShowInternalChat(planConfigs.plan.useInternalChat);
        setShowExternalApi(planConfigs.plan.useExternalApi);
      } catch (e) {}
    }
    fetchData();
  }, []);

  useEffect(() => {
    const fetchChats = async () => {
      try {
        const { data } = await api.get("/chats/", { params: { pageNumber: 1 } });
        dispatch({ type: "LOAD_CHATS", payload: data.records });
      } catch (err) {}
    };
    fetchChats();
  }, []);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    socket.on(`company-${companyId}-chat`, (data) => {
      if (data.action === "new-message" || data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: data });
      }
    });
    return () => socket.disconnect();
  }, [socketManager]);

  useEffect(() => {
    let unreads = 0;
    chats.forEach((chat) => {
      if (chat.users && chat.users.length > 0) {
        chat.users.forEach((u) => {
          if (u.userId === user.id) unreads += u.unreads || 0;
        });
      }
    });
    setHasUnreadChats(unreads > 0);
  }, [chats, user.id]);

  useEffect(() => {
    const check = () => {
      if (whatsApps.length > 0) {
        const offline = whatsApps.filter((w) =>
          ["qrcode", "pairingcode", "PAIRING", "DISCONNECTED", "TIMEOUT", "OPENING"].includes(w.status)
        );
        setConnectionWarning(offline.length > 0);
      }
    };
    const t = setTimeout(check, 2000);
    return () => clearTimeout(t);
  }, [whatsApps]);

  useEffect(() => {
    if (localStorage.getItem("cshow")) setShowCampaigns(true);
  }, []);

  return (
    <Box className={`${classes.container} ${collapsed ? classes.containerCollapsedMain : ""}`} onClick={drawerClose}>
      <Box className={classes.navWrapper}>
        <List component="nav" disablePadding>
          
          {/* ===== MENU PRINCIPAL ===== */}
          {!collapsed && (
            <Box className={classes.sectionLabel}>
              <Typography className={classes.sectionText}>Menu</Typography>
            </Box>
          )}

          <Can
            role={user.profile}
            perform="dashboard:view"
            yes={() => (
              <MenuItem
                to="/"
                icon={<DashboardOutlinedIcon />}
                label="Dashboard"
                collapsed={collapsed}
                iconClass={classes.iconIndigo}
              />
            )}
          />

          <MenuItem
            to="/tickets"
            icon={<WhatsAppIcon />}
            label={i18n.t("mainDrawer.listItems.tickets")}
            collapsed={collapsed}
            iconClass={classes.iconGreen}
          />

          {showKanban && (
            <MenuItem
              to="/kanban"
              icon={<TableChartIcon />}
              label="Kanban"
              collapsed={collapsed}
              iconClass={classes.iconPurple}
            />
          )}

          <MenuItem
            to="/quick-messages"
            icon={<FlashOnIcon />}
            label={i18n.t("mainDrawer.listItems.quickMessages")}
            collapsed={collapsed}
            iconClass={classes.iconOrange}
          />

          <MenuItem
            to="/todolist"
            icon={<BorderColorIcon />}
            label={i18n.t("mainDrawer.listItems.tasks")}
            collapsed={collapsed}
            iconClass={classes.iconPink}
          />

          <MenuItem
            to="/contacts"
            icon={<ContactPhoneOutlinedIcon />}
            label={i18n.t("mainDrawer.listItems.contacts")}
            collapsed={collapsed}
            iconClass={classes.iconBlue}
          />

          <MenuItem
            to="/schedules"
            icon={<EventIcon />}
            label={i18n.t("mainDrawer.listItems.schedules")}
            collapsed={collapsed}
            iconClass={classes.iconTeal}
          />

          <MenuItem
            to="/tags"
            icon={<LocalOfferIcon />}
            label={i18n.t("mainDrawer.listItems.tags")}
            collapsed={collapsed}
            iconClass={classes.iconRed}
          />

          <MenuItem
            to="/chats"
            icon={<ForumIcon />}
            label={i18n.t("mainDrawer.listItems.chats")}
            collapsed={collapsed}
            iconClass={classes.iconCyan}
            dotBadge={hasUnreadChats}
          />

          <MenuItem
            to="/helps"
            icon={<HelpOutlineIcon />}
            label={i18n.t("mainDrawer.listItems.helps")}
            collapsed={collapsed}
            iconClass={classes.iconGray}
          />

          {/* ===== ADMINISTRAÇÃO ===== */}
          <Can
            role={user.profile}
            perform="drawer-admin-items:view"
            yes={() => (
              <>
                {!collapsed && (
                  <Box className={classes.sectionLabel} style={{ marginTop: 16 }}>
                    <Typography className={classes.sectionText}>
                      {i18n.t("mainDrawer.listItems.administration")}
                    </Typography>
                  </Box>
                )}

                {/* Campanhas */}
                {showCampaigns && (
                  <>
                    {collapsed ? (
                      <Tooltip title={i18n.t("mainDrawer.listItems.campaigns")} placement="right" arrow TransitionComponent={Zoom}>
                        <ListItem
                          button
                          onClick={() => history.push("/campaigns")}
                          className={`${classes.menuItem} ${classes.menuItemCollapsed}`}
                        >
                          <ListItemIcon className={`${classes.menuIcon} ${classes.iconGreen}`}>
                            <EventAvailableIcon />
                          </ListItemIcon>
                        </ListItem>
                      </Tooltip>
                    ) : (
                      <>
                        <ListItem
                          button
                          onClick={() => setOpenCampaigns(!openCampaigns)}
                          className={classes.menuItem}
                        >
                          <ListItemIcon className={`${classes.menuIcon} ${classes.iconGreen}`}>
                            <EventAvailableIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={i18n.t("mainDrawer.listItems.campaigns")}
                            className={classes.menuText}
                          />
                          <ExpandMoreIcon
                            className={`${classes.expandIcon} ${openCampaigns ? classes.expandIconOpen : ""}`}
                          />
                        </ListItem>
                        <Collapse in={openCampaigns}>
                          <List disablePadding className={classes.submenu}>
                            <ListItem button onClick={() => history.push("/campaigns")} className={classes.submenuItem}>
                              <ListItemIcon className={classes.submenuIcon}><ListIcon /></ListItemIcon>
                              <ListItemText primary="Listagem" className={classes.submenuText} />
                            </ListItem>
                            <ListItem button onClick={() => history.push("/contact-lists")} className={classes.submenuItem}>
                              <ListItemIcon className={classes.submenuIcon}><PeopleIcon /></ListItemIcon>
                              <ListItemText primary="Listas de Contatos" className={classes.submenuText} />
                            </ListItem>
                            <ListItem button onClick={() => history.push("/campaigns-config")} className={classes.submenuItem}>
                              <ListItemIcon className={classes.submenuIcon}><SettingsOutlinedIcon /></ListItemIcon>
                              <ListItemText primary="Configurações" className={classes.submenuText} />
                            </ListItem>
                          </List>
                        </Collapse>
                      </>
                    )}

                    {/* Fluxos */}
                    {collapsed ? (
                      <Tooltip title={i18n.t("mainDrawer.listItems.flows")} placement="right" arrow TransitionComponent={Zoom}>
                        <ListItem
                          button
                          onClick={() => history.push("/flowbuilders")}
                          className={`${classes.menuItem} ${classes.menuItemCollapsed}`}
                        >
                          <ListItemIcon className={`${classes.menuIcon} ${classes.iconPurple}`}>
                            <AccountTree />
                          </ListItemIcon>
                        </ListItem>
                      </Tooltip>
                    ) : (
                      <>
                        <ListItem
                          button
                          onClick={() => setOpenFlows(!openFlows)}
                          className={classes.menuItem}
                        >
                          <ListItemIcon className={`${classes.menuIcon} ${classes.iconPurple}`}>
                            <AccountTree />
                          </ListItemIcon>
                          <ListItemText
                            primary={i18n.t("mainDrawer.listItems.flows")}
                            className={classes.menuText}
                          />
                          <ExpandMoreIcon
                            className={`${classes.expandIcon} ${openFlows ? classes.expandIconOpen : ""}`}
                          />
                        </ListItem>
                        <Collapse in={openFlows}>
                          <List disablePadding className={classes.submenu}>
                            <ListItem button onClick={() => history.push("/phrase-lists")} className={classes.submenuItem}>
                              <ListItemIcon className={classes.submenuIcon}><EventAvailableIcon /></ListItemIcon>
                              <ListItemText primary="Campanha" className={classes.submenuText} />
                            </ListItem>
                            <ListItem button onClick={() => history.push("/flowbuilders")} className={classes.submenuItem}>
                              <ListItemIcon className={classes.submenuIcon}><ShapeLine /></ListItemIcon>
                              <ListItemText primary="Conversa" className={classes.submenuText} />
                            </ListItem>
                          </List>
                        </Collapse>
                      </>
                    )}
                  </>
                )}

                {user.super && (
                  <MenuItem
                    to="/announcements"
                    icon={<AnnouncementIcon />}
                    label={i18n.t("mainDrawer.listItems.annoucements")}
                    collapsed={collapsed}
                    iconClass={classes.iconOrange}
                  />
                )}

                {showOpenAi && (
                  <MenuItem
                    to="/prompts"
                    icon={<AllInclusive />}
                    label={i18n.t("mainDrawer.listItems.prompts")}
                    collapsed={collapsed}
                    iconClass={classes.iconIndigo}
                  />
                )}

                {showIntegrations && (
                  <MenuItem
                    to="/queue-integration"
                    icon={<DeviceHubOutlined />}
                    label={i18n.t("mainDrawer.listItems.queueIntegration")}
                    collapsed={collapsed}
                    iconClass={classes.iconCyan}
                  />
                )}

                <MenuItem
                  to="/connections"
                  icon={<SyncAltIcon />}
                  label={i18n.t("mainDrawer.listItems.connections")}
                  collapsed={collapsed}
                  iconClass={classes.iconGreen}
                  warningBadge={connectionWarning}
                />

                <MenuItem
                  to="/files"
                  icon={<AttachFile />}
                  label={i18n.t("mainDrawer.listItems.files")}
                  collapsed={collapsed}
                  iconClass={classes.iconLime}
                />

                <MenuItem
                  to="/queues"
                  icon={<AccountTreeOutlinedIcon />}
                  label={i18n.t("mainDrawer.listItems.queues")}
                  collapsed={collapsed}
                  iconClass={classes.iconPink}
                />

                <MenuItem
                  to="/users"
                  icon={<PeopleAltOutlinedIcon />}
                  label={i18n.t("mainDrawer.listItems.users")}
                  collapsed={collapsed}
                  iconClass={classes.iconPurple}
                />

                {showExternalApi && (
                  <MenuItem
                    to="/messages-api"
                    icon={<CodeRoundedIcon />}
                    label={i18n.t("mainDrawer.listItems.messagesAPI")}
                    collapsed={collapsed}
                    iconClass={classes.iconGray}
                  />
                )}

                <MenuItem
                  to="/financeiro"
                  icon={<LocalAtmIcon />}
                  label={i18n.t("mainDrawer.listItems.financeiro")}
                  collapsed={collapsed}
                  iconClass={classes.iconGreen}
                />

                <MenuItem
                  to="/settings"
                  icon={<SettingsOutlinedIcon />}
                  label={i18n.t("mainDrawer.listItems.settings")}
                  collapsed={collapsed}
                  iconClass={classes.iconGray}
                />
              </>
            )}
          />
        </List>
      </Box>

      {/* Footer */}
      {!collapsed && (
        <Box className={classes.footer}>
          <Box className={classes.footerContent}>
            <Box className={classes.footerDot} />
            <Typography className={classes.footerText}>
              Omni v8.0.1
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MainListItems;
